import type { PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { existsSync } from 'node:fs';
import path from 'node:path';
import PDFDocument from 'pdfkit';
import { formatMoney } from '@roovers/shared';
import { HttpError } from '../../core/errors.js';
import { DOC_LABEL, docInclude, isoOf, type DocKind } from './documents.service.js';

const fmtDate = (d: Date | null | undefined) => {
  if (!d) return '';
  const [y, m, day] = d.toISOString().slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

/** The company's own logo if one is set, else the Roovers wordmark shipped in server/assets. */
function findLogo(logoPath: string | null): string | null {
  const candidates = [
    logoPath,
    path.resolve('assets/logo.png'),
    path.resolve('server/assets/logo.png'),
  ];
  for (const c of candidates) if (c && /\.(png|jpe?g)$/i.test(c) && existsSync(c)) return c;
  return null;
}

interface PdfCompany {
  name: string;
  tin: string | null;
  vatNumber: string | null;
  vatRegistered: boolean;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  brandColor: string;
  logoPath: string | null;
  paymentInstructions: string | null;
}

interface PdfLine {
  description: string;
  qty: string;
  unit: string;
  price: string;
  discount: string;
  vat: string;
  total: string;
  optional?: boolean;
  section?: boolean;
}

interface PdfModel {
  company: PdfCompany;
  title: string;
  number: string;
  meta: [string, string][];
  billTo: { name: string; lines: string[] };
  /** Heading above the counterparty block; defaults to BILL TO. */
  partyLabel?: string;
  lines: PdfLine[];
  totals: [string, string][];
  notes: string | null;
  terms: string | null;
  showPayment: boolean;
  statusLabel?: string;
}

const MARGIN = 40;
const PAGE_W = 595.28;
const CONTENT_W = PAGE_W - MARGIN * 2;

/** Renders an A4 PDF. In tests the streams are left uncompressed so text can be searched. */
export function renderPdf(model: PdfModel): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    const doc = new PDFDocument({
      size: 'A4',
      margin: MARGIN,
      compress: process.env.NODE_ENV !== 'test',
      info: { Title: `${model.title} ${model.number}`, Author: model.company.name },
    });
    const chunks: Buffer[] = [];
    doc.on('data', (c: Buffer) => chunks.push(c));
    doc.on('end', () => resolve(Buffer.concat(chunks)));
    doc.on('error', reject);

    const brand = /^#[0-9a-fA-F]{6}$/.test(model.company.brandColor)
      ? model.company.brandColor
      : '#CC0000';
    const grey = '#5B6B82';

    // Header band with company details
    doc.rect(0, 0, PAGE_W, 8).fill(brand);
    const logo = findLogo(model.company.logoPath);
    if (logo) {
      doc.image(logo, MARGIN, 24, { fit: [190, 30] });
      doc.y = 60;
    } else {
      doc
        .fillColor('#0F172A')
        .font('Helvetica-Bold')
        .fontSize(16)
        .text(model.company.name, MARGIN, 28, { width: 300 });
    }
    doc.font('Helvetica').fontSize(9).fillColor(grey);
    const companyLines = [
      model.company.address,
      [model.company.phone, model.company.email].filter(Boolean).join('  |  '),
      model.company.website,
      model.company.tin ? `TIN: ${model.company.tin}` : null,
      model.company.vatNumber ? `VAT No: ${model.company.vatNumber}` : null,
    ].filter(Boolean) as string[];
    doc.text(companyLines.join('\n'), MARGIN, doc.y + 2, { width: 300 });

    doc
      .font('Helvetica-Bold')
      .fontSize(20)
      .fillColor(brand)
      .text(model.title.toUpperCase(), 330, 28, { width: PAGE_W - MARGIN - 330, align: 'right' });
    doc
      .font('Helvetica-Bold')
      .fontSize(11)
      .fillColor('#0F172A')
      .text(model.number, 330, 54, { width: PAGE_W - MARGIN - 330, align: 'right' });
    if (model.statusLabel)
      doc
        .font('Helvetica')
        .fontSize(9)
        .fillColor(grey)
        .text(model.statusLabel, 330, 70, { width: PAGE_W - MARGIN - 330, align: 'right' });

    // Bill to and meta
    const top = Math.max(doc.y + 14, 128);
    doc
      .font('Helvetica-Bold')
      .fontSize(9)
      .fillColor(grey)
      .text(model.partyLabel ?? 'BILL TO', MARGIN, top);
    doc
      .font('Helvetica-Bold')
      .fontSize(11)
      .fillColor('#0F172A')
      .text(model.billTo.name, MARGIN, top + 13, { width: 260 });
    doc
      .font('Helvetica')
      .fontSize(9)
      .fillColor('#0F172A')
      .text(model.billTo.lines.join('\n'), MARGIN, doc.y + 1, { width: 260 });
    const leftEnd = doc.y;

    let my = top;
    for (const [label, value] of model.meta) {
      doc.font('Helvetica').fontSize(9).fillColor(grey).text(label, 340, my, { width: 90 });
      doc
        .font('Helvetica-Bold')
        .fontSize(9)
        .fillColor('#0F172A')
        .text(value, 430, my, { width: PAGE_W - MARGIN - 430, align: 'right' });
      my += 14;
    }

    // Lines table
    let y = Math.max(leftEnd, my) + 18;
    const cols = { desc: MARGIN, qty: 300, price: 340, disc: 405, vat: 450, total: 490 };
    const header = () => {
      doc.rect(MARGIN, y, CONTENT_W, 20).fill(brand);
      doc.font('Helvetica-Bold').fontSize(8.5).fillColor('#FFFFFF');
      doc.text('Description', cols.desc + 6, y + 6, { width: 250 });
      doc.text('Qty', cols.qty, y + 6, { width: 36, align: 'right' });
      doc.text('Unit price', cols.price, y + 6, { width: 60, align: 'right' });
      doc.text('Disc.', cols.disc, y + 6, { width: 40, align: 'right' });
      doc.text('VAT', cols.vat, y + 6, { width: 35, align: 'right' });
      doc.text('Amount', cols.total, y + 6, {
        width: PAGE_W - MARGIN - cols.total - 6,
        align: 'right',
      });
      y += 24;
    };
    header();
    for (const l of model.lines) {
      const h = Math.max(16, doc.heightOfString(l.description, { width: 250 }) + 8);
      if (y + h > 760) {
        doc.addPage();
        y = MARGIN;
        header();
      }
      if (l.section) {
        doc
          .font('Helvetica-Bold')
          .fontSize(9.5)
          .fillColor('#0F172A')
          .text(l.description, cols.desc + 6, y + 3, { width: CONTENT_W - 12 });
      } else {
        doc
          .font('Helvetica')
          .fontSize(9)
          .fillColor(l.optional ? grey : '#0F172A');
        doc.text(l.description + (l.optional ? '  (optional)' : ''), cols.desc + 6, y + 3, {
          width: 250,
        });
        doc.text(l.qty + (l.unit ? ` ${l.unit}` : ''), cols.qty - 10, y + 3, {
          width: 46,
          align: 'right',
        });
        doc.text(l.price, cols.price, y + 3, { width: 60, align: 'right' });
        doc.text(l.discount, cols.disc, y + 3, { width: 40, align: 'right' });
        doc.text(l.vat, cols.vat, y + 3, { width: 35, align: 'right' });
        doc.text(l.total, cols.total, y + 3, {
          width: PAGE_W - MARGIN - cols.total - 6,
          align: 'right',
        });
      }
      doc
        .moveTo(MARGIN, y + h)
        .lineTo(PAGE_W - MARGIN, y + h)
        .lineWidth(0.5)
        .strokeColor('#E3E6EB')
        .stroke();
      y += h;
    }

    // Totals
    y += 10;
    if (y > 700) {
      doc.addPage();
      y = MARGIN;
    }
    for (const [label, value] of model.totals) {
      const strong = label.startsWith('Total') || label.startsWith('Balance');
      doc
        .font(strong ? 'Helvetica-Bold' : 'Helvetica')
        .fontSize(strong ? 11 : 9.5)
        .fillColor('#0F172A');
      doc.text(label, 340, y, { width: 130 });
      doc.text(value, 440, y, { width: PAGE_W - MARGIN - 440, align: 'right' });
      y += strong ? 18 : 14;
    }

    // Notes, payment details, terms
    y += 8;
    const block = (title: string, body: string | null) => {
      if (!body) return;
      if (y > 730) {
        doc.addPage();
        y = MARGIN;
      }
      doc.font('Helvetica-Bold').fontSize(9).fillColor(brand).text(title, MARGIN, y);
      doc
        .font('Helvetica')
        .fontSize(9)
        .fillColor('#0F172A')
        .text(body, MARGIN, doc.y + 2, { width: CONTENT_W });
      y = doc.y + 10;
    };
    block('NOTES', model.notes);
    if (model.showPayment) block('HOW TO PAY', model.company.paymentInstructions);
    block('TERMS', model.terms);

    doc
      .font('Helvetica')
      .fontSize(8)
      .fillColor(grey)
      .text(
        `${model.company.name}${model.company.tin ? '  |  TIN ' + model.company.tin : ''}`,
        MARGIN,
        800,
        { width: CONTENT_W, align: 'center' },
      );
    doc.end();
  });
}

async function company(db: PrismaClient, companyId: number): Promise<PdfCompany> {
  const c = await db.company.findUniqueOrThrow({ where: { id: companyId } });
  return {
    name: c.name,
    tin: c.tin,
    vatNumber: c.vatNumber,
    vatRegistered: c.vatRegistered,
    address: c.address,
    phone: c.phone,
    email: c.email,
    website: c.website,
    brandColor: c.brandColor,
    logoPath: c.logoPath,
    paymentInstructions: c.paymentInstructions,
  };
}

const partyLines = (
  p: { tin: string | null; address: string | null; city: string | null; country: string } | null,
) =>
  [
    p?.address,
    [p?.city, p?.country].filter(Boolean).join(', '),
    p?.tin ? `TIN: ${p.tin}` : null,
  ].filter(Boolean) as string[];

export async function renderDocumentPdf(
  db: PrismaClient,
  companyId: number,
  kind: DocKind | 'receipt' | 'purchaseorder' | 'payout',
  id: number,
): Promise<{ pdf: Buffer; filename: string }> {
  const co = await company(db, companyId);
  if (kind === 'purchaseorder' || kind === 'payout') {
    const { renderPurchasePdf } = await import('../purchases/purchases.pdf.js');
    return renderPurchasePdf(db, companyId, kind, id, { co, renderPdf });
  }
  if (kind === 'receipt') {
    const r = await db.paymentReceipt.findFirst({
      where: { id, companyId },
      include: { allocations: true },
    });
    if (!r) throw new HttpError(404, 'NOT_FOUND', 'Receipt not found');
    const party = await db.party.findUnique({ where: { id: r.partyId } });
    const invoices = await db.salesDocument.findMany({
      where: { id: { in: r.allocations.map((a) => a.invoiceId) } },
      select: { id: true, number: true },
    });
    const account = await db.financialAccount.findUnique({ where: { id: r.financialAccountId } });
    const money = (v: Decimal.Value | { toString(): string }) =>
      formatMoney(v.toString(), r.currency);
    const pdf = await renderPdf({
      company: co,
      title: 'Payment receipt',
      number: r.receiptNo,
      statusLabel: r.status === 'void' ? 'VOID' : undefined,
      meta: [
        ['Date', fmtDate(r.receiptDate)],
        ['Received into', account?.name ?? ''],
        ['Reference', r.reference ?? ''],
      ],
      billTo: { name: party?.name ?? '', lines: partyLines(party) },
      lines: [
        ...r.allocations.map((a) => ({
          description: `Payment for invoice ${invoices.find((i) => i.id === a.invoiceId)?.number ?? ''}`,
          qty: '1',
          unit: '',
          price: money(a.amount),
          discount: '',
          vat: '',
          total: money(a.amount),
        })),
        ...(new Decimal(r.advanceAmount.toString()).gt(0)
          ? [
              {
                description: 'Advance / credit on account',
                qty: '1',
                unit: '',
                price: money(r.advanceAmount),
                discount: '',
                vat: '',
                total: money(r.advanceAmount),
              },
            ]
          : []),
      ],
      totals: [
        ...(new Decimal(r.whtWithheld.toString()).gt(0)
          ? [['Tax withheld by you', money(r.whtWithheld)] as [string, string]]
          : []),
        ['Total received', money(r.amount)],
      ],
      notes: r.notes,
      terms: null,
      showPayment: false,
    });
    return { pdf, filename: `${r.receiptNo}.pdf` };
  }

  const d = await db.salesDocument.findFirst({ where: { id, companyId }, include: docInclude });
  if (!d) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
  const party = await db.party.findUnique({ where: { id: d.partyId } });
  const money = (v: Decimal.Value | { toString(): string }) =>
    formatMoney(v.toString(), d.currency);
  const number = d.number ?? 'DRAFT';
  const taxInvoice = kind === 'invoice' && co.vatRegistered;
  const title = kind === 'invoice' ? (taxInvoice ? 'Tax invoice' : 'Invoice') : DOC_LABEL[kind];
  const meta: [string, string][] = [['Date', fmtDate(d.issueDate)]];
  if (d.dueDate) meta.push(['Due date', fmtDate(d.dueDate)]);
  if (d.validUntil) meta.push(['Valid until', fmtDate(d.validUntil)]);
  if (d.reference) meta.push(['Reference / PO', d.reference]);
  if (d.currency !== 'UGX')
    meta.push([
      'Exchange rate',
      `1 ${d.currency} = UGX ${new Decimal(d.exchangeRate.toString()).toString()}`,
    ]);
  const owed = Decimal.max(
    0,
    new Decimal(d.total.toString())
      .minus(d.amountPaid.toString())
      .minus(d.amountCredited.toString()),
  );
  const totals: [string, string][] = [['Subtotal', money(d.subtotal)]];
  if (new Decimal(d.discountTotal.toString()).gt(0))
    totals.push(['Discounts included', money(d.discountTotal)]);
  totals.push(
    [taxInvoice || new Decimal(d.taxTotal.toString()).gt(0) ? 'VAT' : 'VAT', money(d.taxTotal)],
    [`Total ${d.currency}`, money(d.total)],
  );
  if (
    kind === 'invoice' &&
    (new Decimal(d.amountPaid.toString()).gt(0) || new Decimal(d.amountCredited.toString()).gt(0))
  ) {
    totals.push(
      ['Paid', money(d.amountPaid)],
      ['Credited', money(d.amountCredited)],
      ['Balance due', money(owed)],
    );
  }
  const pdf = await renderPdf({
    company: co,
    title,
    number,
    statusLabel:
      d.status === 'void'
        ? 'VOID'
        : d.status === 'paid'
          ? 'PAID'
          : d.status === 'draft'
            ? 'DRAFT'
            : undefined,
    meta,
    billTo: { name: party?.name ?? '', lines: partyLines(party) },
    lines: d.lines.map((l) => ({
      description: l.description,
      section: l.kind === 'section',
      optional: l.isOptional,
      qty: new Decimal(l.quantity.toString()).toString(),
      unit: l.unit ?? '',
      price: money(l.unitPrice),
      discount:
        l.discountType === 'percent'
          ? `${new Decimal(l.discountValue.toString())}%`
          : l.discountType === 'amount'
            ? money(l.discountValue)
            : '',
      vat: `${new Decimal(l.vatRate.toString())}%`,
      total: l.isOptional && !l.isSelected ? '—' : money(l.lineNet),
    })),
    totals,
    notes: d.notes,
    terms: d.terms,
    showPayment: kind === 'invoice' || kind === 'proforma',
  });
  void isoOf;
  return { pdf, filename: `${number}.pdf` };
}
